<template>
    <v-container fluid>
        <v-row justify="center">
            <v-col cols="12">
                <v-card>
                    <v-card-title class="d-block text-center font-weight-bold primary warning--text py-2" style="word-break: normal">
                        GANADORES DEL SORTEO
                    </v-card-title>
                </v-card>
            </v-col>
        </v-row>
        <v-row justify="center">
            <v-col cols="12">
                <v-card>
                    <v-card-text class="pb-0">
                        <v-row justify="center" align="center">
                            <v-col cols="12" sm="4" md="3">
                                <v-text-field v-model="filters.month" type="month" label="Mes" prepend-icon="mdi-calendar" hide-details/>
                            </v-col>
                            <v-col cols="12" sm="4" md="3">
                                <v-select v-model="filters.period" :items="periods" label="Periodo" prepend-icon="mdi-calendar-range" hide-details/>
                            </v-col>
                            <v-col cols="12" sm="4" md="3" v-if="user.role === 'Administrator'">
                                <v-btn color="success" block large depressed :loading="drawing" @click="openDrawDialog">
                                    <v-icon>mdi-seal-variant</v-icon>&nbsp;&nbsp;Realizar Sorteo
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-card-text>
                    <v-card-title>
                        <v-text-field v-model="search" append-icon="mdi-magnify" label="Buscar" single-line hide-details/>
                        <v-btn color="primary" text :loading="loading" @click="getData">
                            <v-icon>mdi-sync</v-icon>&nbsp;&nbsp;Actualizar
                        </v-btn>
                    </v-card-title>
                    <v-data-table :headers="headers" :items="desserts" :search="search" :loading="loading" :footer-props="footerProps" no-data-text="No hay ganadores registrados">
                        <template v-slot:item.period="{ item }">
                            {{ formatDate(item.period_start) }} - {{ formatDate(item.period_end) }}
                        </template>
                        <template v-slot:item.total_usd="{ item }">
                            <strong>{{ Number(item.total_usd).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }} USD</strong>
                        </template>
                        <template v-slot:item.drawn_at="{ item }">
                            {{ new Date(item.drawn_at) | moment('DD/MM/YYYY HH:mm') }}
                        </template>
                        <template v-slot:item.claim_deadline_at="{ item }">
                            {{ item.claim_deadline_at ? (new Date(item.claim_deadline_at) | moment('DD/MM/YYYY HH:mm')) : '' }}
                        </template>
                        <template v-slot:item.status="{ item }">
                            <v-chip small :color="statusColor(item.status)" dark>{{ statusText(item.status) }}</v-chip>
                        </template>
                        <template v-slot:item.actions="{ item }">
                            <v-btn color="success" small depressed class="ma-1" :loading="loadingAction === item.id + ':claim'" :disabled="item.status !== 'pending'" @click="claim(item)">
                                <v-icon small>mdi-check-circle</v-icon>
                            </v-btn>
                            <v-btn color="warning" small depressed class="ma-1" :loading="loadingAction === item.id + ':reassign'" :disabled="item.status !== 'pending'" @click="reassign(item)">
                                <v-icon small>mdi-refresh</v-icon>
                            </v-btn>
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>
        </v-row>

        <v-dialog v-model="drawDialog" max-width="760" persistent>
            <v-card class="draw-card">
                <v-card-title class="draw-header" style="word-break: normal">
                    <div>
                        <div class="draw-eyebrow">Sorteo quincenal</div>
                        <h2>Realizar sorteo</h2>
                    </div>
                    <v-spacer/>
                    <v-btn icon dark :disabled="drawing" @click="closeDrawDialog">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>

                <v-card-text class="pa-6">
                    <div v-if="drawStep === 'preview'">
                        <v-alert type="info" outlined>
                            Revisa el periodo antes de confirmar. Al realizar el sorteo se seleccionara un ganador aleatorio y se enviara un correo de felicitacion.
                        </v-alert>

                        <v-row dense>
                            <v-col cols="12" sm="4">
                                <v-card outlined class="draw-stat">
                                    <v-icon color="primary">mdi-calendar-range</v-icon>
                                    <span>Periodo</span>
                                    <strong>{{ previewPeriod ? previewPeriod.label : 'Cargando...' }}</strong>
                                </v-card>
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-card outlined class="draw-stat">
                                    <v-icon color="primary">mdi-account-group</v-icon>
                                    <span>Participantes</span>
                                    <strong>{{ previewParticipants.length }}</strong>
                                </v-card>
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-card outlined class="draw-stat">
                                    <v-icon color="primary">mdi-cash-check</v-icon>
                                    <span>Minimo</span>
                                    <strong>{{ previewThreshold }} USD</strong>
                                </v-card>
                            </v-col>
                        </v-row>

                        <v-skeleton-loader type="table" v-if="previewLoading"/>
                        <v-data-table
                            v-else
                            dense
                            :headers="previewHeaders"
                            :items="previewParticipants"
                            :items-per-page="5"
                            class="mt-4"
                            no-data-text="No hay participantes validos para este periodo"
                        >
                            <template v-slot:item.total_usd="{ item }">
                                <strong>{{ Number(item.total_usd).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }} USD</strong>
                            </template>
                        </v-data-table>
                    </div>

                    <div v-if="drawStep === 'drawing'" class="draw-stage">
                        <div class="draw-orbit">
                            <span v-for="n in 12" :key="n" :style="{ transform: 'rotate(' + (n * 30) + 'deg) translateY(-118px)' }"></span>
                            <div class="draw-prize">
                                <v-icon size="54" color="secondary">mdi-gift-open-outline</v-icon>
                            </div>
                        </div>
                        <h2>Seleccionando ganador...</h2>
                        <p>El sistema esta mezclando los participantes validos del periodo.</p>
                        <v-progress-linear indeterminate rounded color="warning" height="8"/>
                    </div>

                    <div v-if="drawStep === 'result' && drawResult">
                        <v-alert :type="drawResult.v === 'success' ? 'success' : 'warning'" outlined>
                            {{ drawResult.status }}
                        </v-alert>

                        <v-card outlined class="winner-card" v-if="drawResult.winner">
                            <div class="winner-ribbon">Ganador</div>
                            <v-avatar size="84" class="winner-avatar">
                                <v-icon size="48">mdi-trophy-award</v-icon>
                            </v-avatar>
                            <h2>{{ drawResult.winner.name }}</h2>
                            <p>{{ drawResult.winner_message }}</p>
                            <v-row dense justify="center">
                                <v-col cols="12" sm="4">
                                    <strong>{{ Number(drawResult.winner.total_usd).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }} USD</strong>
                                    <span>Total acumulado</span>
                                </v-col>
                                <v-col cols="12" sm="4">
                                    <strong>{{ drawResult.participants_count || previewParticipants.length }}</strong>
                                    <span>Participantes</span>
                                </v-col>
                                <v-col cols="12" sm="4">
                                    <strong>{{ drawResult.email && drawResult.email.sent ? 'Enviado' : 'Pendiente' }}</strong>
                                    <span>Correo</span>
                                </v-col>
                            </v-row>
                        </v-card>

                        <v-alert class="mt-4" :type="drawResult.email && drawResult.email.sent ? 'success' : 'warning'" outlined v-if="drawResult.email">
                            {{ drawResult.email.status }}
                        </v-alert>
                    </div>
                </v-card-text>

                <v-card-actions class="px-6 pb-6">
                    <v-spacer/>
                    <v-btn text :disabled="drawing" @click="closeDrawDialog">
                        {{ drawStep === 'result' ? 'Cerrar' : 'Cancelar' }}
                    </v-btn>
                    <v-btn color="primary" depressed :loading="previewLoading" :disabled="drawing" @click="fetchDrawPreview" v-if="drawStep === 'preview'">
                        <v-icon small>mdi-sync</v-icon>&nbsp;&nbsp;Actualizar previa
                    </v-btn>
                    <v-btn color="success" depressed :loading="drawing" :disabled="previewLoading || previewParticipants.length === 0" @click="draw" v-if="drawStep === 'preview'">
                        <v-icon small>mdi-shuffle-variant</v-icon>&nbsp;&nbsp;Confirmar sorteo
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import axios from 'axios';
import Form from '../../../helpers/form';

export default {
    name: 'Winners',
    mixins: [Form],
    props: ['user'],
    data: () => ({
        loading: false,
        drawing: false,
        loadingAction: null,
        drawDialog: false,
        drawStep: 'preview',
        previewLoading: false,
        previewParticipants: [],
        previewPeriod: null,
        previewThreshold: 50,
        drawResult: null,
        search: '',
        filters: {
            month: null,
            period: null
        },
        periods: [
            { text: '1 al 15', value: 'first' },
            { text: '16 al ultimo dia', value: 'second' }
        ],
        headers: [
            { text: 'Periodo', value: 'period', sortable: false },
            { text: 'Origen', value: 'source', sortable: false },
            { text: 'ID Cliente', value: 'user_id', sortable: false },
            { text: 'Ganador', value: 'name', sortable: false },
            { text: 'Total USD', value: 'total_usd', align: 'end', sortable: true },
            { text: 'Fecha Sorteo', value: 'drawn_at', sortable: true },
            { text: 'Limite Reclamo', value: 'claim_deadline_at', sortable: true },
            { text: 'Estado', value: 'status', sortable: true },
            { text: 'Acciones', value: 'actions', sortable: false, align: 'center' }
        ],
        previewHeaders: [
            { text: 'Cliente', value: 'name', sortable: false },
            { text: 'Origen', value: 'source', sortable: false },
            { text: 'ID', value: 'user_id', sortable: false },
            { text: 'Total USD', value: 'total_usd', align: 'end', sortable: true },
            { text: 'Operaciones', value: 'transfers_count', align: 'center', sortable: true }
        ],
        desserts: [],
        footerProps: {
            itemsPerPageOptions: [50, 100, 250, 500, -1]
        }
    }),
    mounted() {
        this.setDefaultPeriod();
        this.getData();
    },
    methods: {
        setDefaultPeriod() {
            const now = new Date();
            this.filters.month = now.toISOString().slice(0, 7);
            this.filters.period = now.getDate() > 15 ? 'second' : 'first';
        },
        getData() {
            this.loading = true;
            axios.get('/api/lottery/winners').then(res => {
                this.desserts = res.data.winners;
            }).catch(err => {
                this.handleErrors(err.response.data.errors);
            }).then(() => {
                this.loading = false;
            });
        },
        openDrawDialog() {
            this.drawDialog = true;
            this.drawStep = 'preview';
            this.drawResult = null;
            this.fetchDrawPreview();
        },
        closeDrawDialog() {
            if (this.drawing) {
                return;
            }

            this.drawDialog = false;
        },
        fetchDrawPreview() {
            this.previewLoading = true;
            axios.get('/api/lottery/participants', { params: this.filters }).then(res => {
                this.previewParticipants = res.data.participants;
                this.previewPeriod = res.data.period;
                this.previewThreshold = res.data.threshold_usd;
            }).catch(err => {
                this.handleErrors(err.response.data.errors);
            }).then(() => {
                this.previewLoading = false;
            });
        },
        draw() {
            this.drawing = true;
            this.drawStep = 'drawing';
            axios.post('/api/lottery/draw', this.filters).then(res => {
                this.drawResult = res.data;
                if (res.data.v === 'success') {
                    this.$toast.success(res.data.status, { icon: 'mdi-check-circle' });
                } else {
                    this.$toast.warning(res.data.status, { icon: 'mdi-information', timeout: 8000 });
                }
                this.getData();
            }).catch(err => {
                this.drawResult = {
                    v: 'warning',
                    status: err.response && err.response.data && err.response.data.status
                        ? err.response.data.status
                        : 'No se pudo completar el sorteo. Intenta nuevamente.',
                    email: null,
                    winner: null
                };
                this.handleErrors(err.response && err.response.data ? err.response.data.errors : null);
            }).then(() => {
                this.drawing = false;
                this.drawStep = 'result';
            });
        },
        formatDate(date) {
            if (!date) return '';
            const [year, month, day] = date.split('-');
            return `${day}/${month}/${year}`;
        },
        statusText(status) {
            const statuses = {
                pending: 'Pendiente',
                claimed: 'Reclamado',
                unclaimed: 'No reclamado'
            };
            return statuses[status] || status;
        },
        statusColor(status) {
            const statuses = {
                pending: 'warning',
                claimed: 'success',
                unclaimed: 'error'
            };
            return statuses[status] || 'grey';
        },
        claim(item) {
            this.loadingAction = item.id + ':claim';
            axios.post('/api/lottery/winners/' + item.id + '/claim').then(res => {
                if (res.data.v === 'success') {
                    this.$toast.success(res.data.status, { icon: 'mdi-check-circle' });
                } else {
                    this.$toast.warning(res.data.status, { icon: 'mdi-information', timeout: 8000 });
                }
                this.getData();
            }).catch(err => {
                this.handleErrors(err.response.data.errors);
            }).then(() => {
                this.loadingAction = null;
            });
        },
        reassign(item) {
            this.loadingAction = item.id + ':reassign';
            axios.post('/api/lottery/winners/' + item.id + '/reassign').then(res => {
                if (res.data.v === 'success') {
                    this.$toast.success(res.data.status, { icon: 'mdi-check-circle' });
                    if (res.data.email) {
                        if (res.data.email.sent) {
                            this.$toast.success(res.data.email.status, { icon: 'mdi-email', timeout: 8000 });
                        } else {
                            this.$toast.warning(res.data.email.status, { icon: 'mdi-email', timeout: 8000 });
                        }
                    }
                } else {
                    this.$toast.warning(res.data.status, { icon: 'mdi-information', timeout: 8000 });
                }
                this.getData();
            }).catch(err => {
                this.handleErrors(err.response.data.errors);
            }).then(() => {
                this.loadingAction = null;
            });
        }
    }
}
</script>

<style scoped>
.draw-card {
    border-radius: 22px;
    overflow: hidden;
}

.draw-header {
    background: var(--v-primary-base);
    color: #fff;
    padding: 26px 30px;
}

.draw-header h2 {
    color: #fff;
    font-size: 1.7rem;
    font-weight: 900;
    margin: 4px 0 0;
}

.draw-eyebrow {
    color: var(--v-secondary-base);
    font-size: 0.76rem;
    font-weight: 900;
    letter-spacing: 0.16em;
    text-transform: uppercase;
}

.draw-stat {
    border-radius: 16px;
    display: flex;
    flex-direction: column;
    min-height: 124px;
    padding: 18px;
}

.draw-stat span {
    color: #667085;
    font-size: 0.78rem;
    font-weight: 800;
    margin-top: 12px;
    text-transform: uppercase;
}

.draw-stat strong {
    color: var(--v-primary-base);
    font-size: 1.1rem;
    line-height: 1.25;
}

.draw-stage {
    align-items: center;
    display: flex;
    flex-direction: column;
    min-height: 430px;
    justify-content: center;
    text-align: center;
}

.draw-stage h2 {
    color: var(--v-primary-base);
    font-size: 2rem;
    font-weight: 900;
    margin-top: 24px;
}

.draw-stage p {
    color: #667085;
    margin-bottom: 24px;
}

.draw-orbit {
    animation: draw-spin 2.4s linear infinite;
    border: 1px dashed var(--v-primary-base);
    border-radius: 50%;
    height: 260px;
    position: relative;
    width: 260px;
}

.draw-orbit span {
    background: var(--v-secondary-base);
    border-radius: 999px;
    height: 14px;
    left: 123px;
    position: absolute;
    top: 123px;
    width: 14px;
}

.draw-prize {
    align-items: center;
    animation: draw-pulse 1s ease-in-out infinite alternate;
    background: var(--v-primary-base);
    border-radius: 50%;
    box-shadow: 0 20px 54px rgba(0, 0, 0, 0.24);
    display: flex;
    height: 112px;
    justify-content: center;
    left: 74px;
    position: absolute;
    top: 74px;
    width: 112px;
}

.winner-card {
    background: #fff;
    border-radius: 22px;
    overflow: hidden;
    padding: 28px;
    position: relative;
    text-align: center;
}

.winner-ribbon {
    background: var(--v-secondary-base);
    color: var(--v-primary-base);
    font-size: 0.78rem;
    font-weight: 900;
    letter-spacing: 0.16em;
    padding: 8px 22px;
    position: absolute;
    right: -34px;
    text-transform: uppercase;
    top: 24px;
    transform: rotate(35deg);
    width: 170px;
}

.winner-avatar {
    background: var(--v-primary-base);
    color: var(--v-secondary-base);
    margin-bottom: 16px;
}

.winner-card h2 {
    color: var(--v-primary-base);
    font-size: 2rem;
    font-weight: 900;
}

.winner-card p {
    color: #475467;
    font-size: 1rem;
    margin: 0 auto 20px;
    max-width: 620px;
}

.winner-card strong,
.winner-card span {
    display: block;
}

.winner-card strong {
    color: var(--v-primary-base);
    font-size: 1.1rem;
}

.winner-card span {
    color: #667085;
    font-size: 0.78rem;
    font-weight: 800;
    text-transform: uppercase;
}

@keyframes draw-spin {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}

@keyframes draw-pulse {
    from {
        transform: scale(0.94);
    }
    to {
        transform: scale(1.04);
    }
}

@media (max-width: 600px) {
    .draw-header {
        padding: 22px;
    }

    .draw-orbit {
        height: 210px;
        width: 210px;
    }

    .draw-orbit span {
        left: 98px;
        top: 98px;
    }

    .draw-prize {
        height: 94px;
        left: 58px;
        top: 58px;
        width: 94px;
    }
}
</style>
